import { useMemo, useState } from 'react';
import { GrClose } from 'react-icons/gr';
import { IconContext } from 'react-icons';
import { useQuery } from '@apollo/client';

import { MAINTENANCES_QUERY } from '../queries';
import {
  compareAsc,
  differenceInHours,
  isPast,
  isFuture,
  format,
} from 'date-fns';

export default function ScheduledMaintenanceBanner() {
  const [modalOpen, setModalOpen] = useState(false);

  const { data } = useQuery(MAINTENANCES_QUERY, {
    pollInterval: 60000,
    fetchPolicy: 'network-only',
  });
  const maintenances = data?.maintenances;

  function handleCancel() {
    setModalOpen(false);
  }

  const parseDate = (dateStr) => {
    if (!dateStr) return null;
    const num = Number(dateStr);
    return isNaN(num) ? new Date(dateStr) : new Date(num);
  };

  const upcomingMaintenance = useMemo(() => {
    if (maintenances) {
      const now = new Date();

      const maintenanceInNext4Hrs = maintenances.filter(
        ({ startDate, endDate }) => {
          const start = parseDate(startDate);
          const end = parseDate(endDate);
          return (
            (isFuture(start) || isPast(start)) &&
            differenceInHours(start, now) < 4 &&
            (end == null || !isPast(end))
          );
        }
      );

      if (maintenanceInNext4Hrs?.length > 0) {
        const sortedMaintenances = maintenanceInNext4Hrs.sort(
          (a, b) => parseDate(a.startDate) - parseDate(b.startDate)
        );
        return sortedMaintenances?.[0];
      }
    }
  }, [maintenances]);

  if (!upcomingMaintenance) {
    return null;
  }

  return (
    <>
      <div
        className="bg-secondary border-2 border-secondary hover:border-white text-white px-3 py-2 mx-3 rounded-lg mb-6 -mt-6 cursor-pointer"
        onClick={() => setModalOpen((prevState) => !prevState)}
      >
        <p className="text-sm font-bold">Upcoming Maintenance</p>
        <p className="text-sm">Learn More</p>
      </div>
      {modalOpen && (
        <div
          className={`fixed max-w-full max-h-full inset-0 z-40 flex justify-center items-center transition-all duration-300 ease-in-out ${
            modalOpen ? 'opacity-100 scale-100' : 'opacity-0 scale-125'
          }`}
        >
          <div className="z-50 w-2/5 bg-secondary text-white rounded-half p-5 min-w-min min-h-min overflow-y-auto">
            <div className="h-full w-full flex flex-col">
              <div className="flex flex-grow-0 w-full justify-end">
                <button className="text-xl" onClick={handleCancel}>
                  <IconContext.Provider
                    value={{ className: 'fill-current text-white' }}
                  >
                    <GrClose />
                  </IconContext.Provider>
                </button>
              </div>
              <div className="flex flex-col flex-grow justify-center items-start pt-8 pb-12 px-12">
                <h4 className="self-center">Upcoming Scheduled Maintenance</h4>
                <div className="space-y-2 mb-6">
                  <p>
                    Periodically, we update our data set to provide you with the
                    best audiences possible.
                  </p>
                  <p>
                    During this time the platform will be unavailable. Please
                    plan accordingly.
                  </p>
                </div>
                <div className="space-y-1">
                  <p>
                    Start time:{' '}
                    {upcomingMaintenance?.startDate &&
                      format(
                        parseDate(upcomingMaintenance?.startDate),
                        'PPP ppp'
                      )}
                  </p>
                  {upcomingMaintenance?.endDate && (
                    <p>
                      End time:{' '}
                      {format(
                        parseDate(upcomingMaintenance?.endDate),
                        'PPP ppp'
                      )}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
          <button
            className="absolute h-screen w-screen bg-gray-500 opacity-50 cursor-default"
            onClick={handleCancel}
          ></button>
        </div>
      )}
    </>
  );
}
